import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../ui/Button';
import { formatBytes } from '../utils';
import axiosInstance from '../../../api/axiosInstance';

export default function ValidationStep({ method, source, credentials, selectedUsers, csvUsers, onUpdate, onNext, showNotification }) {
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleValidate = async () => {
    setValidating(true);
    setError(null);

    try {
      const payload = method === 'bulk'
        ? { migrationType: 'bulk', csvUsers, credentials: csvUsers[0] || {}, sourceType: source?.id }
        : { migrationType: 'individual', selectedUsers, credentials, sourceType: source?.id };

      const response = await axiosInstance.post('/api/business/email-migration/validate-sizes', payload);

      if (response.data.success) {
        setResult(response.data.validation);
        onUpdate(response.data.validation);
      } else {
        setError(response.data.error || 'Validation failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Auto-validate once on mount
  const hasAutoValidated = useRef(false);
  useEffect(() => {
    if (!hasAutoValidated.current) {
      hasAutoValidated.current = true;
      handleValidate();
    }
  }, []); // eslint-disable-line

  const userCount = method === 'bulk' ? csvUsers?.length : selectedUsers?.length;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Validation</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Checking mailbox sizes for {userCount} user{userCount !== 1 ? 's' : ''}
        </p>
      </div>

      {validating && (
        <div className="flex flex-col items-center py-8">
          <svg className="w-8 h-8 text-orange-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} className="opacity-25" />
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
          </svg>
          <p className="text-sm text-gray-500 mt-3">Checking mailbox sizes...</p>
          <p className="text-xs text-gray-400 mt-1">This may take a moment</p>
        </div>
      )}

      {error && (
        <div className="space-y-3">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
          <Button variant="secondary" onClick={handleValidate}>
            Retry Validation
          </Button>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{result.successCount || 0}</div>
              <div className="text-xs text-gray-500 mt-1">Users Validated</div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{result.totalSizeFormatted || formatBytes(result.totalSize)}</div>
              <div className="text-xs text-gray-500 mt-1">Total Size</div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{result.totalUsers || userCount}</div>
              <div className="text-xs text-gray-500 mt-1">Total Users</div>
            </div>
          </div>

          {/* Per-user sizes */}
          {result.userSizes && result.userSizes.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {result.userSizes.map((user, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 text-gray-900 dark:text-white font-mono text-xs">{user.email}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{user.sizeFormatted || formatBytes(user.size)}</td>
                      <td className="px-3 py-2 text-center">
                        {user.status === 'success' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">OK</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={onNext} disabled={!result.valid}>
              Continue
            </Button>
            <Button variant="ghost" onClick={handleValidate}>
              Re-validate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
